import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../App'
import { supabase } from '../lib/supabase'
import { fetchOwnedLayeringPage, fetchPairingCollection, partitionOwnedPairings } from '../lib/ownedPairings'
import { createLatestRequestScope, recommendationScopeKey } from '../lib/requestScope'
import { subscribeCollectionAdded } from '../lib/collectionEvents'

const EMPTY_RECOMMENDATIONS = Object.freeze([])
const empty = () => ({ scope: '', recommendations: [], loading: false, loadingMore: false, error: null, hasMore: false, offset: 0, revision: 0 })
const emptyDiscovery = () => ({scope:'',requested:false,loading:false,error:null,rows:[]})

export function useLayeringRecommendations(catalogId, limit = 20, {enabled = true} = {}) {
  const { user } = useAuth()
  const activeScope = enabled ? recommendationScopeKey(user?.id, catalogId) : ''
  const currentScope = useRef(activeScope)
  currentScope.current = activeScope
  const [state, setState] = useState(empty)
  const [discovery, setDiscovery] = useState(emptyDiscovery)
  const [refreshToken, setRefreshToken] = useState(0)
  const requests = useRef(createLatestRequestScope())
  const discoveryRequests = useRef(createLatestRequestScope())
  const pageLock = useRef(null)
  const stateRef = useRef(state)
  stateRef.current = state
  const pageSize = Math.max(1, Math.min(50, Number(limit) || 20))
  const refresh = useCallback(() => {
    requests.current.invalidate()
    discoveryRequests.current.invalidate()
    pageLock.current = null
    setState(previous => ({...empty(), revision: previous.revision + 1}))
    setDiscovery(emptyDiscovery())
    setRefreshToken(value => value + 1)
  }, [])

  const fetchPage = useCallback(async (append = false) => {
    const scope = activeScope
    if (!scope || pageLock.current?.key === scope) return false
    const previous = stateRef.current
    if (append && (previous.scope !== scope || !previous.hasMore || previous.loading)) return false
    const request = requests.current.begin(scope)
    pageLock.current = request
    const offset = append ? previous.offset : 0
    setState(value => append ? {...value,loadingMore:true,error:null} : {...empty(),scope,loading:true,revision:value.revision+1})
    const isCurrent = () => currentScope.current === scope && requests.current.isCurrent(request)
    try {
      const collection = await fetchPairingCollection(supabase, user.id)
      const result = await fetchOwnedLayeringPage(supabase, {
        userId: user.id,
        targetCatalogId: catalogId,
        pageSize,
        offsetCount: offset,
        collection,
      })
      if (!isCurrent()) return false
      setState(value => {
        const rows = append ? [...value.recommendations, ...result.rows] : result.rows
        return {...value,scope,recommendations:rows.filter((row,index) => rows.findIndex(other => other.catalog_id === row.catalog_id) === index),loading:false,loadingMore:false,error:null,hasMore:result.hasMore,offset:result.offset}
      })
      return true
    } catch {
      if (isCurrent()) setState(value => ({...value,scope,loading:false,loadingMore:false,error:'Collection matches are unavailable. Try again.'}))
      return false
    } finally {
      if (pageLock.current === request) pageLock.current = null
    }
  }, [activeScope,catalogId,pageSize,user?.id])

  useEffect(() => {
    requests.current.invalidate()
    discoveryRequests.current.invalidate()
    pageLock.current = null
    setDiscovery(emptyDiscovery())
    if (activeScope) void fetchPage()
    else setState(empty())
    return () => {requests.current.invalidate(); discoveryRequests.current.invalidate(); pageLock.current = null}
  }, [fetchPage,activeScope,refreshToken])

  useEffect(() => {
    if (!activeScope) return
    const unsubscribe = subscribeCollectionAdded(rows => {if(rows.some(row => row.user_id === user?.id)) refresh()})
    const onVisible = () => {if(document.visibilityState === 'visible') refresh()}
    window.addEventListener('focus',refresh)
    document.addEventListener('visibilitychange',onVisible)
    return () => {unsubscribe();window.removeEventListener('focus',refresh);document.removeEventListener('visibilitychange',onVisible)}
  }, [activeScope,user?.id,refresh])

  const loadDiscovery = useCallback(async () => {
    if (!activeScope) return
    const request = discoveryRequests.current.begin(activeScope)
    setDiscovery({scope:activeScope,requested:true,loading:true,error:null,rows:[]})
    try {
      const [collection,result] = await Promise.all([
        fetchPairingCollection(supabase,user.id),
        supabase.rpc('get_layering_recommendations',{target_catalog_id:catalogId,user_id:user.id,limit_count:1000}),
      ])
      if(currentScope.current !== activeScope || !discoveryRequests.current.isCurrent(request)) return
      if(result.error) throw result.error
      const own = collection.filter(row => row.user_id === user.id)
      const anchorId = own.find(row => row.catalog_id === catalogId)?.id
      setDiscovery({scope:activeScope,requested:true,loading:false,error:null,rows:partitionOwnedPairings(result.data || [],own,anchorId,1000).unowned})
    } catch {
      if(currentScope.current === activeScope && discoveryRequests.current.isCurrent(request)) setDiscovery({scope:activeScope,requested:true,loading:false,error:'Catalog discoveries are unavailable. Try again.',rows:[]})
    }
  }, [activeScope,catalogId,user?.id])
  const scopeMatches = Boolean(activeScope) && state.scope === activeScope
  const discoveryMatches = Boolean(activeScope) && discovery.scope === activeScope
  return {
    recommendations:scopeMatches ? state.recommendations : EMPTY_RECOMMENDATIONS,
    unownedRecommendations:discoveryMatches ? discovery.rows : EMPTY_RECOMMENDATIONS,
    loading:Boolean(activeScope) && (!scopeMatches || state.loading),
    loadingMore:scopeMatches && state.loadingMore,
    error:scopeMatches ? state.error : null,
    hasMore:scopeMatches && state.hasMore,
    revision:state.revision,
    loadMore:() => fetchPage(true), refresh,
    discoveryRequested:discoveryMatches && discovery.requested,
    discoveryLoading:discoveryMatches && discovery.loading,
    discoveryError:discoveryMatches ? discovery.error : null,
    loadDiscovery,
  }
}
